// Shared with the server's Host-header resolution (VIP-12037), so browser and
// SSR never disagree on which site a host belongs to.
export const LOCAL_MAPPING = {
  'vicampo.de': 'de',
  'vicampo.at': 'at',
  'vicampo.ch': 'ch',
  'vicampo.test': 'de',
  'vicampo-at.test': 'at',
  'vicampo-ch.test': 'ch'
}

const devMode = import.meta.env.VITE_APP_DEV_MODE === 'ON'
const API_REGEX = /^(www.|fe-app.)?/

class EnvironmentHelper {
  static detectTopLevelDomain() {
    // SiteConfig takes its config map from the TLD but its domain/apiRoot from
    // detectDomain(), so both must come from the same host or an SSR render
    // pairs one shop's siteId, lang and currency with another shop's api root.
    // There is no `location` on the server, so the domain is the source there
    // until site resolution becomes per-request (Host header).
    const hostname = import.meta.env.SSR
      ? EnvironmentHelper.detectDomain()
      : location.hostname
    if (!devMode) {
      return _getTopLevelDomain(hostname)
    }
    return LOCAL_MAPPING[_getDevDomain(hostname)] || 'de'
  }

  static detectDomain() {
    if (import.meta.env.SSR) {
      return import.meta.env.VITE_APP_DOMAIN || 'www.vicampo.de'
    }
    if (devMode) {
      return import.meta.env.VITE_APP_DOMAIN || location.hostname
    }
    return location.hostname.replace('fe-app.', 'www.')
  }

  /**
   * Root URL of the VOS (the admin SPA). VOS is hosted once per environment
   * (vos.vicampo.de, vos.vicampo-test.de, vos.vicampo.test), not on a
   * subdomain of every shop's domain, so the .at/.ch shops share the .de one.
   * Decided by the domain alone: the live preview runs in dev mode against a
   * live domain and must link to live VOS, only the local stack's *.test hosts
   * link to the local one.
   *
   * @param {string} domain - the current site's domain
   */
  static detectVosRoot(domain) {
    const host = String(domain ?? '')
      .replace(/^[a-z]+:\/\//i, '')
      .split(/[:/]/)[0]
    if (host.endsWith('.test')) return 'https://vos.vicampo.test/'
    // Staging runs vicampo-test.de, .at and .ch (platform-iac README), all on one VOS
    if (/(^|\.)vicampo-test\.[a-z]+$/.test(host))
      return 'https://vos.vicampo-test.de/'
    return 'https://vos.vicampo.de/'
  }

  static detectApiDomain(domain) {
    return domain.replace(API_REGEX, 'api.')
  }
}

const _getDevDomain = (hostname) => hostname?.split('.').slice(-2).join('.')
const _getTopLevelDomain = (hostname) => {
  const levels = hostname.split('.')
  return levels.at(-1)
}

export default EnvironmentHelper
